import { Box } from '@chakra-ui/react';
import { RootState, store, useAppSelector } from 'modules/Editor/store';
import { selectIsFlowModeEnabled } from 'modules/Editor/store/editorSettingsSelector';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { WidgetActionState } from 'modules/Editor/store/widgetControlSlice';
import {
  selectActiveWidgets,
  selectHoveredWidgetId,
  selectWidgetActionState,
} from 'modules/Editor/store/widgetSelector';
import { memo, useEffect, useMemo, useRef } from 'react';
import { WidgetType } from 'types/widget.types';
import { calculateWidgetBoundingBox } from 'utils/dom';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

const DISABLED_HOVER_BOX_WIDGET_TYPES = [WidgetType.Line];

export const HoverBox = memo(() => {
  const hoveredWidgetId = useAppSelector(selectHoveredWidgetId);
  const widgetActionState = useAppSelector(selectWidgetActionState);
  const isFlowModeEnabled = useAppSelector(selectIsFlowModeEnabled);

  const showHoverBox = useMemo(() => {
    if (!hoveredWidgetId) return false;

    // For now, only show for flow chart widgets unless flow mode is enabled
    const attachedLines = lineConnectionCache.getAttachedLines(hoveredWidgetId);
    if (!isFlowModeEnabled && attachedLines?.length <= 0) return false;

    // Don't hover while drag or resize
    if (widgetActionState !== WidgetActionState.Default) return false;

    // Don't show hover box if there are multiple active widgets
    const activeWidgets = selectActiveWidgets(store.getState());
    if (activeWidgets.length > 1) return false;

    const activeWidgetId = activeWidgets[0]?.groupId || activeWidgets[0]?.responsiveGroupId || activeWidgets[0]?.id;
    if (hoveredWidgetId === activeWidgetId) return false;

    const widgetType = getWidgetTypeFromId(hoveredWidgetId);
    return !DISABLED_HOVER_BOX_WIDGET_TYPES.includes(widgetType);
  }, [hoveredWidgetId, widgetActionState, isFlowModeEnabled]);

  return (
    <Box data-testid='hover-box-container'>
      <WidgetHoverBox showHoverBox={showHoverBox} />
    </Box>
  );
});

HoverBox.displayName = 'HoverBox';

interface WidgetHoverBoxProps {
  showHoverBox: boolean;
}

const WidgetHoverBox = ({ showHoverBox }: WidgetHoverBoxProps) => {
  const hoveredWidgetId = useAppSelector(selectHoveredWidgetId);
  const hoverBoxRef = useRef<HTMLDivElement>(null);
  /**
   * Effect to update position of hover box and port wrapper when hovered widget id changes
   */
  useEffect(() => {
    const storeState = store.getState() as RootState;
    if (!hoveredWidgetId || !showHoverBox) {
      return;
    }

    if (!hoverBoxRef.current) return;

    const groupId = groupIdCache.getParentIdNested(hoveredWidgetId);
    const widgetData = selectWidget(groupId || hoveredWidgetId)(storeState);
    const widgetPos = calculateWidgetBoundingBox({ widgetId: hoveredWidgetId, groupId, widgetData });
    if (!widgetPos) return;

    const { leftPx, topPx, widthPx, heightPx, rotateDeg } = widgetPos;

    // Update position of hover box
    const hoverBoxEl = hoverBoxRef.current;
    hoverBoxEl.style.left = `${leftPx}px`;
    hoverBoxEl.style.top = `${topPx}px`;
    hoverBoxEl.style.width = `${widthPx}px`;
    hoverBoxEl.style.height = `${heightPx}px`;
    hoverBoxEl.style.transform = `rotate(${rotateDeg}deg)`;
  }, [hoveredWidgetId, showHoverBox]);

  if (!showHoverBox) return null;

  return (
    <>
      {/* div that renders the blue BB around the widget on hover*/}
      <Box
        position='absolute'
        cursor='move'
        ref={hoverBoxRef}
        border='1px solid'
        borderColor='upgrade.500'
        pointerEvents='none'
        zIndex='999'
      />
    </>
  );
};
